import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Autoplay, EffectCube, Pagination } from 'swiper';
import { getTelLinkIfMobile } from '../../../../utils/phoneLink';


SwiperCore.use([Autoplay, EffectCube, Pagination]);

const CreativePreview = ({
  images,
  audioUrl,
  clickUrl,
  className = '',
  type = 'image', // 'image' for cube, 'swiper' for swipe animation
  canRemove,
  removeFile,
  editSessionKey
}) => {
  const [showSwipeHint, setShowSwipeHint] = useState(true);
  const [isAudioMuted, setIsAudioMuted] = useState(true);
  const [audioElement, setAudioElement] = useState(null);
  const [showSwiper, setShowSwiper] = useState(true);

  // Force full unmount/remount of Swiper on editSessionKey change
  useEffect(() => {
    setShowSwiper(false);
    const timer = setTimeout(() => setShowSwiper(true), 30); // 30ms is enough for DOM cleanup
    return () => clearTimeout(timer);
  }, [editSessionKey]);

  useEffect(() => {
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.loop = true;
      setAudioElement(audio);
      return () => {
        audio.pause();
        audio.src = '';
      };
    }
  }, [audioUrl]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSwipeHint(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  const toggleAudio = () => {
    if (audioElement) {
      if (isAudioMuted) {
        audioElement.play();
      } else {
        audioElement.pause();
      }
      setIsAudioMuted(!isAudioMuted);
    }
  };

  return (
    <div className={`relative mx-auto w-[351px] h-[664px] ${className}`}>
      {/* Mobile Frame */}
      <div className="absolute inset-0 rounded-[2rem] border-[10px] border-slate-800 shadow-2xl overflow-hidden bg-black">
        {/* Swiper Container */}
        <div className="relative w-full h-full perspective-[1600px] transition-transform duration-300 ease-in-out transform scale-100">
          {showSwiper && (
            <Swiper
              key={editSessionKey || Date.now()}
              effect={type === 'image' ? 'cube' : undefined}
              grabCursor
              cubeEffect={type === 'image' ? {
                shadow: false,
                slideShadows: false,
                shadowOffset: 20,
                shadowScale: 0.94,
              } : undefined}
              modules={[EffectCube, Autoplay, Pagination]}
              speed={2000}
              loop={true}
              autoplay={{
                delay: 1800,
                disableOnInteraction: true,
              }}
              pagination={type === 'swiper' ? {
                clickable: true,
                bulletClass: 'swiper-pagination-bullet',
                bulletActiveClass: 'swiper-pagination-bullet-active',
              } : false}
              className={`w-full h-full ${type === 'swiper' ? 'swiper-container' : ''}`}
              onTouchMove={(swiper) => {
                swiper.el.classList.remove('scale-in');
                swiper.el.classList.add('scale-out');
              }}
              onTouchEnd={(swiper) => {
                swiper.el.classList.remove('scale-out');
                swiper.el.classList.add('scale-in');
              }}
            >
              {images.map((img, idx) => (
                <SwiperSlide key={idx} className="bg-center bg-cover">
                  <img
                    src={img.preview || img.url}
                    alt={`slide-${idx}`}
                    className="w-full h-full object-cover"
                  />
                  {canRemove && removeFile && (
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); removeFile(idx); }}
                      className="absolute top-3 left-3 bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors duration-200 z-30 shadow-lg"
                      style={{ zIndex: 50 }}
                    >
                      ×
                    </button>
                  )}
                  {clickUrl && (
                    <a
                      href={getTelLinkIfMobile(clickUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute inset-0 z-10"
                      onClick={(e) => {
                        e.preventDefault();
                        window.open(getTelLinkIfMobile(clickUrl), '_blank');
                      }}
                    >
                    </a>
                  )}
                </SwiperSlide>
              ))}
            </Swiper>
          )}

          {/* Audio Control */}
          {audioUrl && (
            <button
              onClick={toggleAudio}
              className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm rounded-full p-2 flex items-center justify-center z-30 hover:bg-black/70 transition-all duration-200"
            >
              {isAudioMuted ? (
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Swipe Hint Overlay */}
      <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 transition-all duration-300 ${showSwipeHint ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        <div className="relative">
          <div className="w-5 h-5 bg-blue-500 rounded-full absolute left-[78px] animate-[expand_2s_infinite]" />
          <div className="w-8 h-8 bg-[url('https://dqmbjov5jawsp.cloudfront.net/amazon-festive-sale/hand-hint.svg')] bg-center bg-contain bg-no-repeat filter invert animate-[spin_2s_infinite] origin-[52%_62%] mb-2.5" />
          <h6 className="bg-red-600 text-xs font-bold text-white px-3 py-1.5 rounded w-[125px] text-center">
            Drag Left / Right
          </h6>
        </div>
      </div>

      {/* Add pagination styles */}
      <style jsx>{`
        .swiper-container {
          position: relative;
        }
        .swiper-pagination {
          position: absolute;
          bottom: 40px;
          left: 0;
          right: 0;
          z-index: 20;
          display: flex;
          justify-content: center;
          gap: 6px;
        }
        .swiper-pagination-bullet {
          width: 12px;
          height: 12px;
          background-color: rgba(0, 0, 0, 0.25);
          border-radius: 50%;
          opacity: 0.5;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .swiper-pagination-bullet-active {
          background-color: rgba(0, 0, 0, 0.7);
          opacity: 1;
        }
      `}</style>
    </div>
  );
};

export default CreativePreview; 